/**
 * EX-595: cobro de citas con datáfono físico (Paylands POSMS).
 *
 * El cobro con datáfono es asíncrono: `POST /api/pos/cobro` manda el importe al terminal y NO
 * crea ninguna venta. Las ventas las registra el backend cuando Paylands confirma el pago, así
 * que el admin espera el desenlace antes de dar nada por hecho.
 *
 * Aquí vive la lógica que no depende de la red: a quién se enruta, qué datáfono se usa y cómo se
 * traduce cada desenlace a un mensaje. El acceso HTTP está en `src/actions/pos.js`.
 */

export const METODO_PAGO_TARJETA = 'Pago Tarjeta';

export const ESTADO_PENDIENTE = 'PENDIENTE';
export const ESTADO_PAGADO = 'PAGADO';
export const ESTADO_RECHAZADO = 'RECHAZADO';
export const ESTADO_CANCELADO = 'CANCELADO';
export const ESTADO_CANCELACION_SOLICITADA = 'CANCELACION_SOLICITADA';
export const ESTADO_PAGADO_TRAS_CANCELACION = 'PAGADO_TRAS_CANCELACION';
export const ESTADO_ERROR_POSTPAGO = 'ERROR_POSTPAGO';
export const ESTADO_EXPIRADO = 'EXPIRADO';
/** Pseudo-estado local: el polling agotó su tiempo sin desenlace. El backend no lo emite. */
export const ESTADO_TIMEOUT = 'TIMEOUT';

/**
 * Estados en los que el cobro ha terminado. `CANCELACION_SOLICITADA` NO está aquí a propósito:
 * pedir la cancelación no cierra el cobro, porque el cliente todavía puede completar el pago en
 * el terminal y ese pago no se puede ignorar.
 */
export const ESTADOS_FINALES = [
    ESTADO_PAGADO,
    ESTADO_RECHAZADO,
    ESTADO_CANCELADO,
    ESTADO_PAGADO_TRAS_CANCELACION,
    ESTADO_ERROR_POSTPAGO,
    ESTADO_EXPIRADO,
];

/**
 * EX-607: los tres desenlaces en los que la tarjeta puede estar cobrada y no hay venta que lo
 * refleje. Son los que lista la pantalla de revisión manual y los únicos que el backend acepta
 * marcar como resueltos o generar su venta.
 */
export const ESTADOS_REVISION_MANUAL = [
    ESTADO_ERROR_POSTPAGO,
    ESTADO_PAGADO_TRAS_CANCELACION,
    ESTADO_EXPIRADO,
];

export const esRevisionManual = (estado) => ESTADOS_REVISION_MANUAL.indexOf(estado) !== -1;

/** Dónde se revisan esos cobros, en los términos en que lo ve el operario. */
export const NOMBRE_PANTALLA_REVISION = 'Cobros a revisar';

/**
 * Quién decide qué se hace con un cobro que dejó dinero fuera de facturación. Es más estrecho que
 * cobrar a propósito: `PROFESSIONAL_ROLE` puede pasar la tarjeta y no revisar. La lista vive aquí
 * y no en cada pantalla para que la ruta, el menú y la carga del contador no puedan discrepar.
 */
export const ROLES_REVISION_COBROS = ['SUPERADMIN_ROLE', 'ADMIN_ROLE', 'BUSINESS_ROLE'];

export const puedeRevisarCobros = (role) => ROLES_REVISION_COBROS.indexOf(role) !== -1;

export const INTERVALO_POLLING_MS = 2500;

/** La orden vive unos 60 s en el terminal; se escucha algo más antes de dar timeout. */
export const TIMEOUT_POLLING_MS = 75000;

/**
 * EX-611: en qué punto está la lectura de `GET /api/pos/sucursales-datafonos`. Que la
 * configuración no se haya podido leer NO es lo mismo que que la sucursal no tenga terminal, y
 * por eso son tres estados y no un booleano: el segundo caso cobra a mano como siempre y el
 * primero no puede decidirse en silencio.
 */
export const RESOLUCION_CARGANDO = 'cargando';
export const RESOLUCION_RESUELTA = 'resuelta';
export const RESOLUCION_ERROR = 'error';

/** Por dónde va un cobro. `sin_resolver` no es un camino: es la ausencia de respuesta. */
export const ENRUTADO_DATAFONO = 'datafono';
export const ENRUTADO_MANUAL = 'manual';
export const ENRUTADO_SIN_RESOLVER = 'sin_resolver';

/**
 * EX-619: hay terminal con el que cobrar, pero no consta si el operario quiere usarlo. Desde
 * EX-622 el modal arranca con `DECISION_VIA_COBRO_INICIAL`, así que este desenlace queda como
 * salvaguarda para una decisión ausente o desconocida.
 *
 * Es un desenlace distinto de `ENRUTADO_SIN_RESOLVER`: allí la duda es si existe datáfono y la
 * respuesta llega del backend; aquí se sabe que existe y lo que falta es la decisión de quien
 * está cobrando.
 */
export const ENRUTADO_PENDIENTE_DECISION = 'pendiente_decision';

/** Las dos salidas que puede elegir el operario cuando la sucursal tiene terminal. */
export const DECISION_DATAFONO = 'datafono';
export const DECISION_MANUAL = 'manual';

/**
 * EX-622: con qué salida arranca cada cobro con tarjeta en una sucursal con terminal. En el uso
 * real el cobro común es el del datáfono, así que viene marcado y "Anotar a mano" queda como
 * elección explícita. Con varios terminales activos el desplegable sigue arrancando vacío: nadie
 * cobra en un terminal que no haya nombrado.
 */
export const DECISION_VIA_COBRO_INICIAL = DECISION_DATAFONO;

/**
 * Datáfonos de la sucursal con los que se puede cobrar. Un datáfono asociado pero inactivo no
 * cuenta: desactivarlos todos es la forma de apagar la integración en esa sucursal.
 */
export const datafonosActivos = (datafonos = []) =>
    (datafonos || []).filter(datafono => datafono?.activo);

/**
 * Datáfonos asociados a una sucursal dentro de la respuesta del endpoint.
 *
 * Una sucursal que no aparezca en una respuesta correcta cuenta como sucursal sin terminal, no
 * como fallo: el endpoint devuelve TODAS las sucursales vivas de la empresa, así que la que falta
 * está borrada y con ella no se cobra en ningún datáfono.
 */
export const datafonosDeSucursal = (sucursales = [], sucursalId) => {
    if (!sucursalId) return [];
    const sucursal = (sucursales || []).find(candidata =>
        String(candidata?._id) === String(sucursalId));
    return sucursal?.datafonos || [];
};

/**
 * EX-614: a qué OTRA sucursal está ya asignado cada datáfono, indexado por `deviceId`.
 *
 * Un terminal es un aparato físico y está en un solo mostrador: si ya está asignado en otra
 * sucursal, aquí no puede ofrecerse como disponible. La sucursal que se está editando se excluye
 * a propósito, para que su propia asignación no se marque a sí misma como ocupada.
 *
 * Sin `sucursalId` (sucursal nueva, que aún no existe) cuentan todas: no hay ninguna asignación
 * propia de la que distinguirlas.
 *
 * Devuelve el nombre y no un booleano porque la pantalla tiene que decir dónde está el terminal:
 * "no aparece" deja al operario buscando un aparato que sí existe.
 */
export const sucursalesPorDatafono = (sucursales = [], sucursalId) => {
    const porDispositivo = new Map();
    (sucursales || []).forEach(sucursal => {
        if (sucursalId && String(sucursal?._id) === String(sucursalId)) return;
        (sucursal?.datafonos || []).forEach(datafono => {
            if (!datafono?.deviceId || porDispositivo.has(datafono.deviceId)) return;
            porDispositivo.set(datafono.deviceId, sucursal?.nombre || '');
        });
    });
    return porDispositivo;
};

/**
 * ¿Se está cobrando con tarjeta algo que aún no está cobrado? En multipago basta con que la
 * tarjeta sea uno de los métodos; solo esa parte va al terminal. Las filas con `ventaId` son
 * pagos ya persistidos (EX-518) y no se vuelven a cobrar.
 */
export const cobraConTarjeta = (metodosPago = []) =>
    metodosPago.some(metodo =>
        (metodo?._id || metodo) === METODO_PAGO_TARJETA && !metodo?.ventaId);

/**
 * ¿Por dónde va el cobro? La integración sigue siendo opt-in por configuración: sin datáfonos
 * activos se cobra a mano exactamente como siempre.
 *
 * EX-611: sin tarjeta de por medio la configuración de datáfonos da igual y el cobro es manual
 * aunque no se haya podido leer — el aviso solo aparece cuando de verdad hay una decisión que
 * tomar. Con tarjeta y sin configuración resuelta no se elige camino: se dice que no se sabe.
 */
export const enrutadoCobro = ({ metodosPago = [], datafonos = [], resolucion, decision = null } = {}) => {
    if (!cobraConTarjeta(metodosPago)) return ENRUTADO_MANUAL;
    if (resolucion !== RESOLUCION_RESUELTA) return ENRUTADO_SIN_RESOLVER;
    if (datafonosActivos(datafonos).length === 0) return ENRUTADO_MANUAL;

    // EX-619: tener un datáfono vinculado no es querer usarlo en cada venta. Con terminal
    // disponible, el camino lo decide el operario en ese cobro. Desde EX-622 el modal arranca en
    // `DECISION_VIA_COBRO_INICIAL`; una decisión ausente o desconocida sigue sin enrutar a ningún
    // sitio como salvaguarda: ni se manda al terminal ni se anota a mano.
    if (decision === DECISION_DATAFONO) return ENRUTADO_DATAFONO;
    if (decision === DECISION_MANUAL) return ENRUTADO_MANUAL;
    return ENRUTADO_PENDIENTE_DECISION;
};

export const MENSAJE_DATAFONOS_CARGANDO = 'Todavía se está comprobando si esta sucursal cobra '
    + 'con datáfono. Espera un momento y vuelve a intentarlo.';

export const MENSAJE_DATAFONOS_NO_RESUELTOS = 'No se ha podido comprobar si esta sucursal cobra '
    + 'con datáfono, así que el pago no se ha anotado: podría tener que cobrarse en el terminal. '
    + 'Vuelve a comprobarlo antes de continuar.';

/** Por qué no se ha podido decidir el enrutado, en los términos del operario. */
export const mensajeEnrutadoSinResolver = (resolucion) =>
    resolucion === RESOLUCION_CARGANDO ? MENSAJE_DATAFONOS_CARGANDO : MENSAJE_DATAFONOS_NO_RESUELTOS;

const DESENLACES = {
    [ESTADO_PAGADO]: {
        tono: 'exito',
        titulo: 'Pago confirmado',
        texto: 'El datáfono ha confirmado el pago.',
        permiteReintento: true,
    },
    [ESTADO_RECHAZADO]: {
        tono: 'error',
        titulo: 'Pago rechazado',
        texto: 'El banco ha rechazado la tarjeta. No se ha registrado ninguna venta.',
        permiteReintento: true,
    },
    [ESTADO_CANCELADO]: {
        tono: 'aviso',
        titulo: 'Cobro cancelado',
        texto: 'El cobro se ha cancelado en el datáfono. No se ha registrado ninguna venta.',
        permiteReintento: true,
    },
    [ESTADO_PAGADO_TRAS_CANCELACION]: {
        tono: 'error',
        titulo: 'Pendiente de revisión manual',
        texto: 'El pago se completó en el datáfono después de pedir la cancelación. La tarjeta '
            + 'está cobrada y la venta no se ha registrado: revísalo antes de volver a cobrar. '
            + `Lo tienes en el menú, en "${NOMBRE_PANTALLA_REVISION}".`,
        permiteReintento: false,
    },
    [ESTADO_ERROR_POSTPAGO]: {
        tono: 'error',
        titulo: 'Pendiente de revisión manual',
        texto: 'El cobro se ha realizado, pero la venta no ha podido registrarse. Revísalo antes '
            + 'de volver a cobrar para no cobrar dos veces al cliente. Lo tienes en el menú, en '
            + `"${NOMBRE_PANTALLA_REVISION}".`,
        permiteReintento: false,
    },
    [ESTADO_EXPIRADO]: {
        tono: 'error',
        titulo: 'Cobro expirado',
        texto: 'La orden ha expirado en el datáfono sin desenlace. Comprueba en el terminal si '
            + 'llegó a cobrarse antes de volver a intentarlo. Lo tienes en el menú, en '
            + `"${NOMBRE_PANTALLA_REVISION}".`,
        permiteReintento: false,
    },
    [ESTADO_TIMEOUT]: {
        tono: 'aviso',
        titulo: 'El cobro sigue en curso',
        texto: 'No hemos recibido respuesta del datáfono. El cobro puede seguir en curso y se '
            + 'comprobará: si se completa, la venta quedará registrada. No vuelvas a cobrar sin '
            + 'revisarlo.',
        permiteReintento: false,
    },
};

const DESENLACE_DESCONOCIDO = {
    tono: 'error',
    titulo: 'No se ha podido completar el cobro',
    texto: 'El cobro ha terminado en un estado inesperado. Comprueba el datáfono antes de volver '
        + 'a intentarlo.',
    permiteReintento: false,
};

/**
 * Mensaje para cada desenlace. `permiteReintento` es lo que decide si el formulario vuelve a
 * quedar editable: en los desenlaces donde la tarjeta ya está cobrada (o puede estarlo) volver
 * al formulario invitaría a lanzar una segunda orden, que sería un segundo cobro real.
 */
export const desenlaceCobro = (estado, detalleError) => {
    const desenlace = DESENLACES[estado] || DESENLACE_DESCONOCIDO;
    if (!detalleError) return { ...desenlace, estado };
    return { ...desenlace, estado, texto: `${desenlace.texto} Detalle: ${detalleError}` };
};

/**
 * EX-607: qué pasó y qué se espera que haga el negocio, por estado. Es copy distinto del de
 * `DESENLACES`: aquel se lee justo después de un cobro fallido y este se lee días más tarde en un
 * listado, sin el contexto de la cita delante.
 *
 * Los tres casos piden acciones distintas y esa es la razón de ser de la pantalla: un cobro que
 * falló al registrar la venta se arregla normalmente registrándola a mano, y uno cobrado tras una
 * cancelación acaba en devolución al cliente. Confundirlos cuesta dinero en las dos direcciones.
 */
const CASOS_REVISION = {
    [ESTADO_ERROR_POSTPAGO]: {
        tono: 'error',
        queHaPasado: 'La tarjeta se cobró y la venta no llegó a registrarse.',
        queHacer: 'Normalmente se resuelve generando la venta desde aquí, que la registra con el '
            + 'importe que ya se cobró.',
    },
    [ESTADO_PAGADO_TRAS_CANCELACION]: {
        tono: 'aviso',
        queHaPasado: 'Se pidió cancelar el cobro y el cliente pasó la tarjeta igualmente: se '
            + 'cobró y no se registró ninguna venta.',
        queHacer: 'Normalmente acaba en una devolución al cliente.',
    },
    [ESTADO_EXPIRADO]: {
        tono: 'aviso',
        queHaPasado: 'La orden expiró en el datáfono sin que llegara su desenlace, así que no se '
            + 'sabe si la tarjeta se cobró.',
        queHacer: 'Comprueba en el terminal si llegó a cobrarse y actúa en consecuencia.',
    },
};

const CASO_DESCONOCIDO = {
    tono: 'error',
    queHaPasado: 'El cobro quedó en un estado que no se reconoce.',
    queHacer: 'Comprueba en el terminal si la tarjeta llegó a cobrarse.',
};

export const casoRevision = (estado) => ({ ...(CASOS_REVISION[estado] || CASO_DESCONOCIDO), estado });

/** Importes en euros, con coma decimal: en España "25.12" se lee como otra cosa. */
export const importeConComa = (importe) => {
    const numero = Number(importe);
    if (!Number.isFinite(numero)) return '';
    return `${numero.toFixed(2).replace('.', ',')} €`;
};

/** Nombre y apellidos de una persona del cobro (cliente o revisor), sin dejar espacios sueltos. */
export const nombreCompletoPersona = (persona) =>
    `${persona?.nombre || ''} ${persona?.apellidos || ''}`.trim();

const CLIENTE_SIN_IDENTIFICAR = 'cliente no identificado';

/**
 * EX-613: cómo se nombra al cliente del cobro en la confirmación. Que no se sepa quién es no
 * impide registrar la venta —el payload guardado sí lo sabe—, pero el operario tiene que verlo:
 * confirmar una venta "a nadie" es distinto de confirmarla a nombre de alguien.
 */
export const nombreClienteCobro = (cliente) =>
    nombreCompletoPersona(cliente) || CLIENTE_SIN_IDENTIFICAR;

/** El texto de la confirmación se inyecta como HTML en SweetAlert2, así que los datos se escapan. */
const escaparHtml = (texto) => String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * EX-613: las dos salidas de la revisión manual, en los términos del operario.
 *
 * Viven juntas y no en la pantalla porque confundirlas cuesta dinero en las dos direcciones: una
 * registra una venta real y la otra no toca nada. Separadas era cuestión de tiempo que una de las
 * dos dejara de decir lo que hace.
 */
export const AVISO_RESOLVER_NO_MUEVE_DINERO = 'Marcar como resuelto <strong>no registra ninguna '
    + 'venta y no devuelve ningún dinero</strong>: solo deja constancia de que alguien se ha hecho '
    + 'cargo. Lo que haya que hacer con el importe se hace por los caminos de siempre.';

export const AVISO_GENERAR_VENTA_EXPIRADO = 'Este cobro expiró sin desenlace, así que '
    + '<strong>no se sabe si la tarjeta llegó a cobrarse</strong>: compruébalo en el terminal '
    + 'antes de generar la venta.';

/**
 * Qué se va a registrar. El importe y el cliente van delante porque son lo que el operario
 * contrasta contra el ticket del terminal antes de dar el paso.
 */
export const avisoGenerarVenta = (cobro = {}) => {
    const lineas = [
        'Se va a registrar la venta de este cobro con el importe que ya se pasó por la tarjeta.',
        `Importe: <strong>${escaparHtml(importeConComa(cobro.importe))}</strong>`,
        `Cliente: <strong>${escaparHtml(nombreClienteCobro(cobro.cliente))}</strong>`,
        'Esto <strong>sí registra una venta</strong>. Si lo que hiciste con el importe fue otra '
            + 'cosa, la salida es "Marcar como resuelto".',
    ];

    if (cobro.estado === ESTADO_EXPIRADO) lineas.splice(1, 0, AVISO_GENERAR_VENTA_EXPIRADO);

    return lineas.join('<br /><br />');
};

export const MENSAJE_INICIO_RECHAZADO = 'No se ha podido iniciar el cobro en el datáfono';

/** El backend responde sin acentos; se compara sobre el texto tal cual lo manda. */
const MSG_VENTA_YA_NO_PENDIENTE = 'no esta pendiente';

const HTTP_CONFLICTO = 409;

/**
 * EX-605: por qué el backend ha rechazado iniciar el cobro sobre una venta pendiente.
 *
 * El `409` es el importante: significa que hay otro cobro vivo contra esa misma venta y que
 * alguien puede estar pasando la tarjeta ahora mismo. No se discrimina por código de estado a
 * secas porque el `400` está sobrecargado (payload inválido, importe 0, datáfono no asociado,
 * falta el id de la venta y también "la venta ya no está pendiente"): ese caso se reconoce por el
 * mensaje. Lo que no está contemplado aquí conserva el mensaje que mande el backend.
 */
export const mensajeInicioCobroRechazado = ({ status, msg } = {}) => {
    if (status === HTTP_CONFLICTO) {
        return 'Hay un cobro en curso contra esta venta: puede que alguien esté pasando la tarjeta '
            + 'en el datáfono ahora mismo. Espera a que termine antes de volver a intentarlo.';
    }
    if (String(msg || '').includes(MSG_VENTA_YA_NO_PENDIENTE)) {
        return 'Esta venta ya se ha cobrado por otra vía y no queda nada pendiente. Cierra y '
            + 'vuelve a abrir la cita para ver su estado real.';
    }
    return msg || MENSAJE_INICIO_RECHAZADO;
};

export const MENSAJE_GENERAR_VENTA_RECHAZADA = 'No se ha podido registrar la venta de este cobro';

export const MENSAJE_GENERAR_VENTA_EN_CURSO = 'Otro usuario está generando la venta de este cobro '
    + 'ahora mismo. Desde aquí no se ha registrado nada: vuelve a cargar el listado para ver cómo '
    + 'ha quedado antes de volver a intentarlo.';

/**
 * EX-613: por qué el backend no ha registrado la venta del cobro.
 *
 * El `409` es el único que se traduce: significa que otra petición se llevó el cerrojo y que
 * desde aquí no se ha registrado nada, y eso no se deduce del mensaje del backend. Lo demás
 * —saldo, stock, la venta ya cobrada por otra vía— llega ya en los términos en los que lo lanza
 * la validación, que es lo que el operario necesita leer.
 */
export const mensajeGenerarVentaRechazado = ({ status, msg } = {}) => {
    if (status === HTTP_CONFLICTO) return MENSAJE_GENERAR_VENTA_EN_CURSO;
    return msg || MENSAJE_GENERAR_VENTA_RECHAZADA;
};

const pausa = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Escucha el cobro hasta que alcanza un estado final o se agota el tiempo.
 *
 * `consultar`, `esperar` y `ahora` se inyectan para poder probar el bucle sin red ni relojes
 * reales. Una consulta que falle no aborta la escucha: un error de red puntual no puede hacer
 * que el admin dé por perdido un cobro que quizá se está completando.
 */
export const esperarDesenlaceCobro = async ({
    consultar,
    onEstado,
    esperar = pausa,
    ahora = Date.now,
    intervaloMs = INTERVALO_POLLING_MS,
    timeoutMs = TIMEOUT_POLLING_MS,
}) => {
    const limite = ahora() + timeoutMs;
    let ultimoEstado = ESTADO_PENDIENTE;

    while (ahora() < limite) {
        await esperar(intervaloMs);
        const respuesta = await consultar();
        const pago = respuesta?.ok ? respuesta.pago : null;

        if (pago?.estado && pago.estado !== ultimoEstado) {
            ultimoEstado = pago.estado;
            if (onEstado) onEstado(pago.estado);
        }

        if (pago && ESTADOS_FINALES.indexOf(pago.estado) !== -1) {
            return {
                estado: pago.estado,
                detalleError: pago.detalleError || '',
                ventasCreadas: pago.ventasCreadas || [],
                // EX-605: con `tipoPayload: 'actualizacion'` no nace ninguna venta y el desenlace
                // se lee aquí. Llega como id, no como documento.
                ventaActualizada: pago.ventaActualizada || null,
            };
        }
    }

    return { estado: ESTADO_TIMEOUT, detalleError: '', ventasCreadas: [], ventaActualizada: null };
};
